import type { TextFieldProps } from '@mui/material';
import { MuiTelInput, matchIsValidTel } from 'mui-tel-input';
import React from 'react';

import FieldLabel from '@components/Common/FieldLabel';
import { normalizeToE164 } from '@helpers/utils';
import './index.scss';

/**
 * Curated set of TextField props we forward. Deliberately does NOT include
 * mui-tel-input's `forceCallingCode`/`defaultCountry` (those are set internally),
 * which keeps the props off the discriminated union that `defaultCountry` selects.
 */
type ForwardedProps = Pick<
  TextFieldProps,
  | 'label'
  | 'fullWidth'
  | 'size'
  | 'required'
  | 'error'
  | 'helperText'
  | 'disabled'
  | 'placeholder'
  | 'className'
  | 'sx'
  | 'name'
  | 'id'
  | 'variant'
  | 'autoComplete'
  | 'autoFocus'
  | 'margin'
>;

export type PhoneInputProps = ForwardedProps & {
  /** Current phone value (E.164, e.g. "+919876543210"). Legacy/partial values are normalized for display. */
  value: string;
  /** Called with the E.164 value (no spaces) on every change. */
  onChange: (value: string) => void;
};

/**
 * Shared international phone input for the Control Center. Wraps mui-tel-input with a
 * searchable country dropdown, India (+91) as the default country, and E.164 output.
 * Use this for EVERY phone field so the extension picker and stored format stay consistent.
 */
const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  className,
  label,
  required,
  ...rest
}) => (
  // The label goes above the field, like every other control in a form —
  // passing it to MuiTelInput draws it inside the notched outline, which is
  // how one field in a form of four labelled itself differently.
  /* The wrapper is not decoration. FormField lays its label and control out in a flex column with
     a gap; this rendered a bare fragment, so it got FieldLabel's margin and nothing else, and the
     phone control sat several pixels higher than the email beside it on the same row. Three of the
     four fields in the create-employer dialog are FormFields, so this one was the odd one out. */
  <div className="cc-phone-field">
    {label && <FieldLabel required={required} disabled={rest.disabled}>{label}</FieldLabel>}
    <MuiTelInput
      // cc-phone sizes the country picker to the field. Left to itself the
      // picker is a 42px IconButton inside a 35px input, which made this the
      // one field on the page that did not line up with its neighbours.
      className={`cc-phone ${className ?? ''}`.trim()}
      defaultCountry="IN"
      MenuProps={{ className: 'cc-phone-menu' }}
      value={normalizeToE164(value)}
      onChange={(next) => onChange((next || '').replace(/\s+/g, ''))}
      required={required}
      {...rest}
    />
  </div>
);

export { matchIsValidTel };
export default PhoneInput;
